import { memo, useCallback } from 'react';
import { useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueType,
} from 'react-select';
import Select from 'react-select/creatable';
import { useFormikContext } from 'formik';
import { GenericInput, GenericInputProps } from '../GenericInput';
import { useReactSelectStyle } from './Select';
import { useTrackReactSelect } from './useReactSelectChangeLog';
import classnames from 'classnames';

export type CreatableAutoCompleteProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = Partial<GenericInputProps> &
  ReactSelectProps<OptionType, IsMulti> & { isMulti: IsMulti };

const InnerComponent = memo(
  <OptionType extends {}, IsMulti extends boolean>({
    className,
    trackingName,
    ...props
  }: ReactSelectProps<OptionType, IsMulti>) => {
    const theme: Theme = useTheme();
    const classes: any = useReactSelectStyle({ ...props, theme });
    const onChange = useTrackReactSelect<OptionType, IsMulti>(
      trackingName,
      props.onChange,
    );
    return (
      <label>
        <Select
          {...props}
          classNamePrefix="react-select"
          className={classnames(classes.picker, {
            small: props.small,
          })}
          onChange={onChange}
        />
      </label>
    );
  },
);

export const CreatableAutoComplete = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
>({
  name,
  onChange,
  ...rest
}: CreatableAutoCompleteProps<OptionType, IsMulti>) => {
  const formikContext = useFormikContext<any>();
  const _onChange = useCallback(
    (value: ValueType<OptionType, IsMulti>) => {
      if (!name) {
        return;
      }
      formikContext.setFieldValue(name, value);
    },
    [name, formikContext], //eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <GenericInput
      component={InnerComponent}
      {...rest}
      name={name}
      onChange={onChange || _onChange}
    />
  );
};
